import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * StreamingContext - Global streaming state coordination
 *
 * This context allows different parts of the application to know when
 * SSE streaming is active, enabling them to pause non-critical operations
 * (like notification polling) that could interfere with the stream.
 */

interface StreamingContextValue {
  /** Whether SSE streaming is currently active */
  isStreaming: boolean;
  /** Signal that streaming has started */
  setStreamingActive: () => void;
  /** Signal that streaming has ended */
  setStreamingInactive: () => void;
}

const StreamingContext = createContext<StreamingContextValue | undefined>(undefined);

interface StreamingProviderProps {
  children: ReactNode;
}

export function StreamingProvider({ children }: StreamingProviderProps) {
  const [isStreaming, setIsStreaming] = useState(false);

  const setStreamingActive = useCallback(() => {
    setIsStreaming(true);
  }, []);

  const setStreamingInactive = useCallback(() => {
    setIsStreaming(false);
  }, []);

  const value: StreamingContextValue = {
    isStreaming,
    setStreamingActive,
    setStreamingInactive,
  };

  return (
    <StreamingContext.Provider value={value}>
      {children}
    </StreamingContext.Provider>
  );
}

/**
 * Hook to access streaming context
 * @throws Error if used outside StreamingProvider
 */
export function useStreamingContext(): StreamingContextValue {
  const context = useContext(StreamingContext);

  if (context === undefined) {
    throw new Error('useStreamingContext must be used within a StreamingProvider');
  }

  return context;
}

/**
 * Hook to check streaming state without throwing
 * Returns isStreaming: false if outside provider
 */
export function useStreamingState(): { isStreaming: boolean } {
  const context = useContext(StreamingContext);
  return { isStreaming: context?.isStreaming ?? false };
}
